/*
    Copyright (c) 2023 Alethea Katherine Flowers.
    Published under the standard MIT License.
    Full text available at: https://opensource.org/licenses/MIT
*/

import { later } from "../../base/async";
import {
  CSS,
  CustomElement,
  attribute,
  css,
  html,
} from "../../base/web-components";
import { KCUIElement } from "../../kc-ui";
import kc_ui_styles from "../../kc-ui/kc-ui.css";
import { CopperLayerNames, HoleLayerNames, LayerNames, LayerSet } from "../../viewers/board/layers";
import type { BoardViewer } from "../../viewers/board/viewer";
import { Project } from "../project";
import { FetchFileSystem, VirtualFileSystem } from "../services/vfs";
import type { KCBoardAppElement } from "./kc-board/app";
import type { KCSchematicAppElement } from "./kc-schematic/app";

/**
 *
 */
class KiCanvasEmbedElement extends KCUIElement {
  static override styles = [
    ...KCUIElement.styles,
    new CSS(kc_ui_styles),
    css`
            :host {
                margin: 0;
                display: flex;
                position: relative;
                width: 100%;
                max-height: 100%;
                aspect-ratio: 1.414;
                background-color: aqua;
                color: var(--fg);
                font-family: "Nunito", ui-rounded, "Hiragino Maru Gothic ProN",
                    Quicksand, Comfortaa, Manjari, "Arial Rounded MT Bold",
                    Calibri, source-sans-pro, sans-serif;
                contain: layout paint;
            }

            main {
                display: contents;
            }

            kc-board-app,
            kc-schematic-app {
                width: 100%;
                height: 100%;
                flex: 1;
            }
        `,
  ];

  constructor() {
    super();
    this.provideContext("project", this.#project);

    const callback: IntersectionObserverCallback = (entries, observer) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting && !this.scrolledIntoView) {
          console.log(`Visibility changed: `, this);
          this.scrolledIntoView = true;
          this.initialContentCallback();
        }
      });
    };
    const observer = new IntersectionObserver(callback);
    observer.observe(this);
  }

  @attribute({ type: Boolean })
  scrolledIntoView: boolean = false;

  #project: Project = new Project();

  @attribute({ type: String })
  src: string | null;

  @attribute({ type: Boolean })
  public loading: boolean;

  @attribute({ type: Boolean })
  public loaded: boolean;

  @attribute({ type: String })
  controls: "none" | "basic" | "basic+" | "full" | null;

  @attribute({ type: String })
  controlslist: string | null;

  @attribute({ type: String })
  theme: string | null;

  @attribute({ type: String })
  zoom: "objects" | "page" | string | null;

  @attribute({ type: Number })
  initialZoom: number | null

  @attribute({ type: Number })
  initialX: number | null

  @attribute({ type: Number })
  initialY: number | null

  @attribute({ type: String })
  layers: string | null;

  custom_resolver: ((name: string) => URL) | null = null;

  #schematic_app: KCSchematicAppElement;
  #board_app: KCBoardAppElement;

  initialized: boolean = false;
  override initialContentCallback() {
    if (!this.initialized) {
      this.initialized = true;
      this.#setup_events();
      later(() => {
        this.#load_src();
      });
    }
  }

  async #setup_events() { }

  async #load_src() {
    const sources = [];

    if (this.src) {
      sources.push(this.src);
    }

    for (const src_elm of this.querySelectorAll<KiCanvasSourceElement>(
      "kicanvas-source",
    )) {
      if (src_elm.src) {
        sources.push(src_elm.src);
      }
    }

    if (sources.length == 0) {
      console.warn("No valid sources specified");
      return;
    }

    const vfs = new FetchFileSystem(sources, this.custom_resolver);
    await this.#setup_project(vfs);
  }

  async #setup_project(vfs: VirtualFileSystem) {
    this.loaded = false;
    this.loading = true;

    try {
      await this.#project.load(vfs);

      this.loaded = true;
      await this.update();

      this.#project.set_active_page(this.#project.root_schematic_page!);
    } finally {
      this.loading = false;
    }
  }

  override render() {
    if (!this.loaded) {
      return html``;
    }

    if (this.#project.has_schematics && !this.#schematic_app && this.scrolledIntoView) {
      this.#schematic_app = html`<kc-schematic-app
                sidebarcollapsed
                controls="${this.controls}"
                controlslist="${this.controlslist}">
            </kc-schematic-app>` as KCSchematicAppElement;
    }

    if (this.#project.has_boards && !this.#board_app && this.scrolledIntoView) {
      this.#board_app = html`<kc-board-app
                sidebarcollapsed
                controls="${this.controls}"
                controlslist="${this.controlslist}">
</kc-board-app>` as KCBoardAppElement;

      this.#board_app.viewerLoaded.then((_) => {
        // Need later here to make sure we're scheduled after the
        // later call in the document-viewer.ts that repositions the
        // camera after the document loads.
        later(async () => {
          const order = [
            ...CopperLayerNames,
            LayerNames.f_adhes,
            LayerNames.b_adhes,
            LayerNames.f_paste,
            LayerNames.b_paste,
            LayerNames.f_silks,
            LayerNames.b_silks,
            LayerNames.f_mask,
            LayerNames.b_mask,
            LayerNames.dwgs_user,
            LayerNames.cmts_user,
            LayerNames.eco1_user,
            LayerNames.eco2_user,
            LayerNames.edge_cuts,
            LayerNames.margin,
            LayerNames.f_crtyd,
            LayerNames.b_crtyd,
            LayerNames.f_fab,
            LayerNames.b_fab,
            LayerNames.user_1,
            LayerNames.user_2,
            LayerNames.user_3,
            LayerNames.user_4,
            LayerNames.user_5,
            LayerNames.user_6,
            LayerNames.user_7,
            LayerNames.user_8,
            LayerNames.user_9,
          ];

          const layers = this.#board_app.viewer.layers;
          if (this.layers === null) {
            for (const layer of layers.in_order()) {
              if (order.indexOf(layer.name as LayerNames) !== -1) {
                layer.visible = layer.name.includes(".Cu");
              }
            }
          } else {
            const holes = this.layers.match("Holes") === null
              ? []
              : HoleLayerNames.map(l => l.toString().trim().toLowerCase());

            const viaLayers = Array.from((layers as LayerSet).via_layers()).map(l => l.name);
            const vias = this.layers.match("Vias") === null
              ? []
              : viaLayers.map(l => l.toString().trim().toLowerCase());

            const padLayers = Array.from((layers as LayerSet).pad_layers()).map(l => l.name);
            const pads = this.layers.match("Pads") === null
              ? []
              : padLayers.map(l => l.toString().trim().toLowerCase());

            const layerOpacities: { [name: string]: number } = {};
            const specifiedLayers = this.layers.split(",").map((layer) => {
              let name = layer.trim().toLowerCase();
              if (layer.includes(":")) {
                name = layer.split(":")[0]?.trim().toLowerCase() ?? name;
                layerOpacities[name] = Number(layer.split(":")[1])
              }

              return name;
            });
            const enabledLayers = [...holes, ...vias, ...pads, ...specifiedLayers];
            for (const layer of layers.in_order()) {
              const name = layer.name.trim().toLowerCase();
              if (layerOpacities[name]) {
                layer.opacity = layerOpacities[name]!;
              }
              layer.visible = enabledLayers.includes(name);
            }
          }

          (this.#board_app.viewer as BoardViewer).zone_opacity = 0.1;
          (this.#board_app.viewer as BoardViewer).zoom_to_board();

          this.#board_app.layersPanel.update_item_states();

          if (this.initialZoom !== null && this.initialX !== null && this.initialY !== null) {
            this.#board_app.viewer.setOptions({
              initialZoom: this.initialZoom,
              initialX: this.initialX,
              initialY: this.initialY,
            });
          }

        });
      });
    }

    const focus_overlay =
      (this.controls ?? "none") == "none" ||
        this.controlslist?.includes("nooverlay")
        ? null
        : html`<kc-ui-focus-overlay></kc-ui-focus-overlay>`;

    return html`<main>
            ${this.#schematic_app} ${this.#board_app} ${focus_overlay}
        </main>`;
  }
}

window.customElements.define("kicanvas-embed", KiCanvasEmbedElement);

class KiCanvasSourceElement extends CustomElement {
  constructor() {
    super();
    this.ariaHidden = "true";
    this.hidden = true;
    this.style.display = "none";
  }

  @attribute({ type: String })
  src: string | null;
}

window.customElements.define("kicanvas-source", KiCanvasSourceElement);

/* Import required fonts.
 * TODO: Package these up as part of KiCanvas
 */
document.body.appendChild(
  html`<link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@48,400,0,0&family=Nunito:wght@300;400;500;600;700&display=swap"
        crossorigin="anonymous" />`,
);
